import React, { useEffect, useMemo, useState } from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useGetTaskTypes } from '../TaskType.hooks';
import { TaskType } from '../TaskType.interface';

function compareNames(
  name: string,
  value: string | string[] | undefined
): boolean {
  return Array.isArray(value) ? value.includes(name) : name === value;
}

export interface TaskTypeSingleSelectorProps {
  value?: string;
  multiple?: false;
  disabled?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  filter?: Partial<TaskType>;
  options?: TaskType[];
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onChange?: (taskType: TaskType | null) => void;
}

export interface TaskTypeMultipleSelectorProps {
  value?: string[];
  multiple: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  filter?: Partial<TaskType>;
  options?: TaskType[];
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onChange?: (taskTypes: TaskType[] | null) => void;
}

export type TaskTypeSelectorProps =
  | TaskTypeSingleSelectorProps
  | TaskTypeMultipleSelectorProps;

const TaskTypeSelector: React.FC<TaskTypeSelectorProps> = (
  props: TaskTypeSelectorProps
) => {
  const {
    value: entityName,
    multiple = false,
    filter,
    options,
    disabled,
    errorMessage,
    textFieldProps,
    isLoading: isLoadingProp = false,
    onChange
  } = props;
  const [taskTypeOptions, setTaskTypeOptions] = useState<TaskType[]>([]);
  const [{ data, isLoading: isFinding, error }, findTaskTypes] =
    useGetTaskTypes();
  const isLoading: boolean = isFinding || isLoadingProp;

  useEffect(() => {
    // no fetch if options exist
    !options && findTaskTypes(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, findTaskTypes]);

  useEffect(() => {
    data && setTaskTypeOptions(data);
  }, [data]);

  useEffect(() => {
    options && setTaskTypeOptions(options);
  }, [options]);

  const taskType: TaskType | TaskType[] | null = useMemo(() => {
    const taskTypes: TaskType[] = taskTypeOptions.filter((option: TaskType) => {
      return compareNames(option.name, entityName);
    });
    return multiple ? taskTypes : taskTypes.shift() || null;
  }, [entityName, taskTypeOptions, multiple]);

  return (
    <Autocomplete
      multiple={multiple}
      loading={isLoading}
      disabled={disabled}
      value={taskType || null}
      options={taskTypeOptions}
      getOptionLabel={(option: TaskType) => {
        return `${option.label} (${option.prefix})`;
      }}
      getOptionSelected={(option: TaskType) =>
        compareNames(option.name, entityName)
      }
      onChange={(_event: React.ChangeEvent<{}>, value) =>
        onChange?.(value as any)
      }
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select Task Type'
          placeholder='Choose a Task Type'
          error={!!(error?.message || errorMessage)}
          helperText={error?.message || errorMessage}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default TaskTypeSelector;
